import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Button,
  Badge,
  Divider,
  Descriptions,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  message,
  Popconfirm,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  PrinterOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import PaymentQr from "../../../assets/all-images/PaymentQR.png";
import { getUserData } from "../../../hooks/Auth";
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const RenterOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const user = getUserData();
  const userEmail = user?.user?.email;
  const [filters, setFilters] = useState({
    status: null,
    payment_status: null,
    search: "",
    date_range: null,
  });

  const printOrderRef = useRef();
  const API_BASE_URL = "https://techcrew.pk/ulc/";

  // Fetch orders with filters
  const fetchOrders = async () => {
    setLoading(true);
    try {
      if (!userEmail) {
        throw new Error("User email not available");
      }

      const queryParams = {
        email: userEmail, // Add email to query params
        ...(filters.status && { status: filters.status }),
        ...(filters.payment_status && { payment_status: filters.payment_status }),
        ...(filters.search && { search: filters.search }),
        ...(filters.date_range && {
          start_date: filters.date_range[0].format("YYYY-MM-DD"),
          end_date: filters.date_range[1].format("YYYY-MM-DD"),
        }),
      };

      const { data } = await axios.get(`${API_BASE_URL}Get_RenterOrders.php`, {
        params: queryParams,
      });

      if (data.status.code === 200) {
        setOrders(data.data);
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      message.error(error.message || "Failed to fetch orders");
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch single order details
  // Fetch single order details
const fetchOrderDetails = async (orderId) => {
  setDetailsLoading(true);
  try {
    const { data } = await axios.get(`${API_BASE_URL}All_Orders.php`, {
      params: { 
        order_id: orderId,
        email: userEmail // Include email in the request
      }
    });

    if (data.status.code === 200) {
      setOrderDetails(data.data);
    } else {
      throw new Error(data.status.message);
    }
  } catch (error) {
    message.error(error.message || "Failed to fetch order details");
  } finally {
    setDetailsLoading(false);
  }
};

  // Handle view order
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewModalVisible(true);
    fetchOrderDetails(order.order_id);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  // Apply filters
  const applyFilters = () => {
    fetchOrders();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: null,
      payment_status: null,
      search: "",
      date_range: null,
    });
    fetchOrders();
  };

  // Print single order
  // Print single order - Updated to match AllOrders implementation
const handlePrintOrder = () => {
  if (!selectedOrder || !orderDetails) {
    message.warning("Please wait while order details load");
    return;
  }

  const printContent = `
    <div style="padding: 30px; font-family: Arial, sans-serif; font-size: 12px;">
      <!-- Header Section -->
      <div style="text-align: center; margin-bottom: 20px;">
        <h1 style="margin: 0; font-size: 24px; font-weight: bold;">UNITED LIMITED COMPANY</h1>
        <p style="font-size: 12px; margin: 5px 0 0 0;">
          8074, Al Sahaba Street, Al Yarmuk District, Riyadh, 13243 Kingdom of Saudi Arabia
        </p>
        <Divider style="margin: 10px 0;" />
        <h2 style="margin: 10px 0; font-size: 18px;">Order Invoice</h2>
      </div>

      <!-- Order Info -->
      <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
        <div>
          <strong>Order ID:</strong> #${orderDetails.order.order_id}
        </div>
        <div>
          <strong>Date:</strong> ${orderDetails.order.order_date}
        </div>
      </div>

      <!-- Customer Info -->
      <div style="margin-bottom: 15px;">
        <strong>Customer:</strong> ${orderDetails.order.customer_name}<br/>
        <strong>Email:</strong> ${orderDetails.order.email}<br/>
        <strong>Phone:</strong> ${orderDetails.order.phone}
      </div>

      <!-- Order Status -->
      <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
        <div>
          <strong>Status:</strong> 
          <span style="color: ${
            orderDetails.order.status === "Completed"
              ? "green"
              : orderDetails.order.status === "Confirmed"
              ? "blue"
              : orderDetails.order.status === "Pending"
              ? "orange"
              : "red"
          }">
            ${orderDetails.order.status}
          </span>
        </div>
        <div>
          <strong>Payment:</strong> 
          <span style="color: ${
            orderDetails.order.payment_status === "Paid"
              ? "green"
              : orderDetails.order.payment_status === "Partially Paid"
              ? "blue"
              : "red"
          }">
            ${orderDetails.order.payment_status}
          </span>
        </div>
      </div>

      <!-- Items Table -->
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #f0f0f0;">
            <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Item</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Category</th>
            <th style="padding: 8px; text-align: right; border: 1px solid #ddd;">Qty</th>
            <th style="padding: 8px; text-align: right; border: 1px solid #ddd;">Unit Price</th>
            <th style="padding: 8px; text-align: right; border: 1px solid #ddd;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${orderDetails.items
            .map(
              (item) => `
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                item.equipment_name
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                item.category_name
              }</td>
              <td style="padding: 8px; text-align: right; border: 1px solid #ddd;">${
                item.quantity
              }</td>
              <td style="padding: 8px; text-align: right; border: 1px solid #ddd;">
                ${parseFloat(item.unit_price).toLocaleString("en-US", {
                  style: "currency",
                  currency: "SAR",
                })}
              </td>
              <td style="padding: 8px; text-align: right; border: 1px solid #ddd;">
                ${parseFloat(item.total_price).toLocaleString("en-US", {
                  style: "currency",
                  currency: "SAR",
                })}
              </td>
            </tr>
            <tr>
              <td colspan="5" style="padding: 4px 8px; border: 1px solid #ddd; font-size: 11px;">
                <strong>Duration:</strong> ${item.duration} ${
                item.rental_type
              }(s) | 
                <strong>Dates:</strong> ${item.start_date} to ${item.end_date}
              </td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>

      <!-- Summary -->
      <div style="margin-left: auto; width: 300px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <strong>Subtotal:</strong>
          <span>
            ${parseFloat(orderDetails.order.grand_total).toLocaleString("en-US", {
              style: "currency",
              currency: "SAR",
            })}
          </span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <strong>Tax:</strong>
          <span>
            ${parseFloat(orderDetails.order.tax_amount).toLocaleString(
              "en-US",
              {
                style: "currency",
                currency: "SAR",
              }
            )}
          </span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 14px; margin-top: 10px;">
          <strong>Grand Total:</strong>
          <strong>
            ${parseFloat(orderDetails.order.grand_total).toLocaleString(
              "en-US",
              {
                style: "currency",
                currency: "SAR",
              }
            )}
          </strong>
        </div>
      </div>

      <!-- Enhanced Payment QR Code Section -->
      <div style="margin-top: 30px; border-top: 1px dashed #ccc; padding-top: 20px;">
        <div style="text-align: center;">
          <div style="display: inline-block; width: 100%; max-width: 500px; border-radius: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%); padding: 20px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
            <h3 style="margin: 0 0 15px 0; font-size: 18px; color: #2c3e50; font-weight: 600;">
              Payment Information
            </h3>
            
            <div style="display: flex; justify-content: center; margin-bottom: 20px;">
              <div style="background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); position: relative;">
                <img src=${PaymentQr} alt="Payment QR Code" style="width: 140px; height: 140px; display: block; margin: 0 auto;"/>
                <div style="position: absolute; top: -10px; right: -10px; background: #1890ff; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold;">
                  <span>!</span>
                </div>
              </div>
            </div>
            
            <div style="margin-bottom: 15px; text-align: center;">
              <p style="margin: 0 0 10px 0; font-size: 14px; color: #555;">
                <strong>Scan this QR code</strong> with your mobile banking app
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <Divider style="margin: 20px 0 10px 0;" />
      <div style="text-align: center; font-size: 11px; color: #666;">
        <p>Thank you for your business!</p>
        <p>UNITED LIMITED COMPANY | 8074, Al Sahaba Street, Al Yarmuk District, Riyadh, 13243 KSA</p>
        <p style="margin-top: 5px;">Tel: +966538077541 | Email: Ksa@unitedlimited.net</p>
      </div>
    </div>
  `;

  // Create an iframe for printing and load all necessary fonts
  const iframe = document.createElement("iframe");
  iframe.style.position = "absolute";
  iframe.style.width = "0px";
  iframe.style.height = "0px";
  iframe.style.border = "none";
  document.body.appendChild(iframe);

  // Write the print content to the iframe with necessary font imports
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <html>
      <head>
        <title>Print</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap" rel="stylesheet">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&display=swap');

          @media print {
            @page {
              margin: 0;
              size: auto;
            }
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-family: 'Open Sans', sans-serif;
            }
            img {
              max-width: 100%;
              height: auto;
              page-break-inside: avoid;
              display: block;
            }
            @page :left, @page :right, @page :first {
              margin-top: 0;
              margin-bottom: 0;
            }
            @page {
              marks: none;
              size: auto;
            }
          }
        </style>
      </head>
      <body>${printContent}</body>
    </html>
  `);
  doc.close();

  // Wait a bit for resources to load before printing
  setTimeout(() => {
    // Trigger the print dialog
    iframe.contentWindow.focus();
    iframe.contentWindow.print();

    // Clean up the iframe after printing
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }, 500);
};

  // Columns configuration
  const columns = [
    {
      title: "Order ID",
      dataIndex: "order_id",
      key: "order_id",
      render: (id) => <Text strong>#{id}</Text>,
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "date",
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "amount",
      render: (amount) => (
        <Text strong>
          {parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: "SAR",
          })}
        </Text>
      ),
      align: "right",
    },
    {
      title: "Items",
      dataIndex: "item_count",
      key: "items",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={
            status === "Completed"
              ? "green"
              : status === "Confirmed"
              ? "blue"
              : status === "Pending"
              ? "orange"
              : "red"
          }
        >
          {status}
        </Tag>
      ),
      filters: [
        { text: "Pending", value: "Pending" },
        { text: "Confirmed", value: "Confirmed" },
        { text: "Completed", value: "Completed" },
        { text: "Cancelled", value: "Cancelled" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Payment",
      dataIndex: "payment_status",
      key: "payment",
      render: (status) => (
        <Badge
          status={
            status === "Paid"
              ? "success"
              : status === "Partially Paid"
              ? "processing"
              : "error"
          }
          text={status}
        />
      ),
      filters: [
        { text: "Paid", value: "Paid" },
        { text: "Unpaid", value: "Unpaid" },
        { text: "Partially Paid", value: "Partially Paid" },
      ],
      onFilter: (value, record) => record.payment_status === value,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewOrder(record)}
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={async () => {
              setSelectedOrder(record);
              try {
                await fetchOrderDetails(record.order_id);
                handlePrintOrder();
              } catch (error) {
                message.error("Failed to load order details for printing");
              }
            }}
          />
        </Space>
      ),
    },
  ];

  // Initial data fetch
  useEffect(() => {
    fetchOrders();
  }, []);

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4}>My Orders</Title>}
        bordered={false}
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={() => fetchOrders()}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: "16px" }}>
          <Col span={6}>
            <Input
              placeholder="Search my orders..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by status"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
            >
              <Option value="Pending">Pending</Option>
              <Option value="Confirmed">Confirmed</Option>
              <Option value="Completed">Completed</Option>
              <Option value="Cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filter by payment"
              allowClear
              value={filters.payment_status}
              onChange={(value) => handleFilterChange("payment_status", value)}
            >
              <Option value="Paid">Paid</Option>
              <Option value="Unpaid">Unpaid</Option>
              <Option value="Partially Paid">Partially Paid</Option>
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: "100%" }}
              onChange={(dates) => handleFilterChange("date_range", dates)}
              value={filters.date_range}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginBottom: "16px" }}>
          <Col span={24} style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={resetFilters}>Reset Filters</Button>
              <Button type="primary" onClick={applyFilters}>
                Apply Filters
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="order_id"
          loading={loading}
          scroll={{ x: true }}
          bordered
        />
      </Card>

      {/* Order Details Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Text strong style={{ fontSize: "18px" }}>
              Order Details
            </Text>
            <Tag
              color={
                orderDetails?.order.status === "Completed"
                  ? "green"
                  : orderDetails?.order.status === "Confirmed"
                  ? "blue"
                  : orderDetails?.order.status === "Pending"
                  ? "orange"
                  : "red"
              }
              style={{ marginLeft: "10px", fontSize: "14px" }}
            >
              #{selectedOrder?.order_id}
            </Tag>
          </div>
        }
        visible={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
        style={{ top: 20 }}
      >
        {detailsLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <SyncOutlined spin style={{ fontSize: "32px", color: "#1890ff" }} />
            <p style={{ marginTop: "16px", fontSize: "16px" }}>
              Loading order details...
            </p>
          </div>
        ) : orderDetails ? (
          <div style={{ padding: "8px" }}>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintOrder}
              style={{ marginBottom: "16px" }}
              loading={detailsLoading}
            >
              Print Order
            </Button>

            {/* Printable content */}
            <div style={{ display: "none" }}>
              <div ref={printOrderRef} style={{ padding: "20px" }}>
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <h1 style={{ margin: 0 }}>UNITED LIMITED COMPANY</h1>
                  <p style={{ margin: "5px 0 0 0" }}>
                    8074, Al Sahaba Street, Al Yarmuk District, Riyadh, 13243
                    Kingdom of Saudi Arabia
                  </p>
                  <Divider style={{ margin: "10px 0" }} />
                  <h2 style={{ margin: "10px 0" }}>Order Invoice</h2>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <strong>Order ID:</strong> #{orderDetails.order.order_id}
                  </div>
                  <div>
                    <strong>Date:</strong> {orderDetails.order.order_date}
                  </div>
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <strong>Customer:</strong> {orderDetails.order.customer_name}
                  <br />
                  <strong>Email:</strong> {orderDetails.order.email}
                  <br />
                  <strong>Phone:</strong> {orderDetails.order.phone}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                  }}
                >
                  <div>
                    <strong>Status:</strong>{" "}
                    <span
                      style={{
                        color:
                          orderDetails.order.status === "Completed"
                            ? "green"
                            : orderDetails.order.status === "Confirmed"
                            ? "blue"
                            : orderDetails.order.status === "Pending"
                            ? "orange"
                            : "red",
                      }}
                    >
                      {orderDetails.order.status}
                    </span>
                  </div>
                  <div>
                    <strong>Payment:</strong>{" "}
                    <span
                      style={{
                        color:
                          orderDetails.order.payment_status === "Paid"
                            ? "green"
                            : orderDetails.order.payment_status ===
                              "Partially Paid"
                            ? "blue"
                            : "red",
                      }}
                    >
                      {orderDetails.order.payment_status}
                    </span>
                  </div>
                </div>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "20px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f0f0f0" }}>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          border: "1px solid #ddd",
                        }}
                      >
                        Item
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          border: "1px solid #ddd",
                        }}
                      >
                        Category
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          border: "1px solid #ddd",
                        }}
                      >
                        Qty
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          border: "1px solid #ddd",
                        }}
                      >
                        Unit Price
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          border: "1px solid #ddd",
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetails.items.map((item) => (
                      <React.Fragment key={item.order_item_id}>
                        <tr>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                            }}
                          >
                            {item.equipment_name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              border: "1px solid #ddd",
                            }}
                          >
                            {item.category_name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              border: "1px solid #ddd",
                            }}
                          >
                            {item.quantity}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              border: "1px solid #ddd",
                            }}
                          >
                            {parseFloat(item.unit_price).toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "SAR",
                              }
                            )}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              border: "1px solid #ddd",
                            }}
                          >
                            {parseFloat(item.total_price).toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "SAR",
                              }
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td
                            colspan="5"
                            style={{
                              padding: "4px 8px",
                              border: "1px solid #ddd",
                              fontSize: "11px",
                            }}
                          >
                            <strong>Duration:</strong> {item.duration}{" "}
                            {item.rental_type}(s) | <strong>Dates:</strong>{" "}
                            {item.start_date} to {item.end_date}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                <div style={{ marginLeft: "auto", width: "300px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                    }}
                  >
                    <strong>Subtotal:</strong>
                    <span>
                      {parseFloat(orderDetails.order.sub_total).toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "SAR",
                        }
                      )}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                    }}
                  >
                    <strong>Tax:</strong>
                    <span>
                      {parseFloat(orderDetails.order.tax_amount).toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "SAR",
                        }
                      )}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "14px",
                      marginTop: "10px",
                    }}
                  >
                    <strong>Grand Total:</strong>
                    <strong>
                      {parseFloat(orderDetails.order.grand_total).toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "SAR",
                        }
                      )}
                    </strong>
                  </div>
                </div>

                {/* Payment QR Code Section */}
                <div
                  style={{
                    marginTop: "30px",
                    borderTop: "1px dashed #ccc",
                    paddingTop: "20px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        display: "inline-block",
                        width: "100%",
                        maxWidth: "500px",
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)",
                        padding: "20px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    >
                      <h3
                        style={{
                          margin: "0 0 15px 0",
                          fontSize: "18px",
                          color: "#2c3e50",
                          fontWeight: "600",
                        }}
                      >
                        Payment Information
                      </h3>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginBottom: "20px",
                        }}
                      >
                        <div
                          style={{
                            background: "white",
                            padding: "15px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            position: "relative",
                          }}
                        >
                          <img
                            src={PaymentQr}
                            alt="Payment QR Code"
                            style={{
                              width: "140px",
                              height: "140px",
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "-10px",
                              right: "-10px",
                              background: "#1890ff",
                              color: "white",
                              borderRadius: "50%",
                              width: "30px",
                              height: "30px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            <span>!</span>
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          marginBottom: "15px",
                          textAlign: "center",
                        }}
                      >
                        <p
                          style={{ margin: "0 0 10px 0", fontSize: "14px" }}
                        >
                          <strong>Scan this QR code</strong> with your mobile
                          banking app to complete payment
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <Divider style={{ margin: "20px 0 10px 0" }} />
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "11px",
                    color: "#666",
                  }}
                >
                  <p>Thank you for your business!</p>
                  <p>
                    UNITED LIMITED COMPANY | 8074, Al Sahaba Street, Al Yarmuk
                    District, Riyadh, 13243 KSA
                  </p>
                  <p style={{ marginTop: "5px" }}>
                    Tel: +966538077541 | Email: Ksa@unitedlimited.net
                  </p>
                </div>
              </div>
            </div>

            {/* Visible content in modal */}
            <Card
              title="Customer & Order Summary"
              bordered={false}
              headStyle={{
                backgroundColor: "#f0f2f5",
                borderBottom: "1px solid #f0f0f0",
              }}
              style={{ marginBottom: "24px", borderRadius: "8px" }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: "16px" }}>
                    <Text
                      strong
                      style={{ display: "block", marginBottom: "4px" }}
                    >
                      Customer Information
                    </Text>
                    <div style={{ paddingLeft: "8px" }}>
                      <Text strong style={{ fontSize: "16px" }}>
                        {orderDetails.order.customer_name}
                      </Text>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "4px",
                        }}
                      >
                        <Text type="secondary" style={{ marginRight: "16px" }}>
                          <span style={{ marginRight: "4px" }}>📧</span>
                          {orderDetails.order.email}
                        </Text>
                        <Text type="secondary">
                          <span style={{ marginRight: "4px" }}>📱</span>
                          {orderDetails.order.phone}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: "16px" }}>
                    <Text
                      strong
                      style={{ display: "block", marginBottom: "4px" }}
                    >
                      Order Information
                    </Text>
                    <div style={{ paddingLeft: "8px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text>Order Date:</Text>
                        <Text strong>{orderDetails.order.order_date}</Text>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "4px",
                        }}
                      >
                        <Text>Status:</Text>
                        <Tag
                          color={
                            orderDetails.order.status === "Completed"
                              ? "green"
                              : orderDetails.order.status === "Confirmed"
                              ? "blue"
                              : orderDetails.order.status === "Pending"
                              ? "orange"
                              : "red"
                          }
                        >
                          {orderDetails.order.status}
                        </Tag>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "4px",
                        }}
                      >
                        <Text>Payment:</Text>
                        <Tag
                          color={
                            orderDetails.order.payment_status === "Paid"
                              ? "green"
                              : orderDetails.order.payment_status ===
                                "Partially Paid"
                              ? "blue"
                              : "red"
                          }
                        >
                          {orderDetails.order.payment_status}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            <Card
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Text strong>Order Items</Text>
                  <Tag
                    color="blue"
                    style={{ marginLeft: "8px", borderRadius: "10px" }}
                  >
                    {orderDetails.items.length} items
                  </Tag>
                </div>
              }
              bordered={false}
              headStyle={{
                backgroundColor: "#f0f2f5",
                borderBottom: "1px solid #f0f0f0",
              }}
              style={{ marginBottom: "24px", borderRadius: "8px" }}
            >
              {orderDetails.items.map((item) => (
                <Card
                  key={item.order_item_id}
                  style={{ marginBottom: "16px", borderRadius: "8px" }}
                  bodyStyle={{ padding: "16px" }}
                >
                  <Row gutter={16} align="middle">
                    <Col span={16}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div>
                          <Text strong style={{ fontSize: "16px" }}>
                            {item.equipment_name}
                          </Text>
                          <div style={{ marginTop: "8px" }}>
                            <Tag color="blue">{item.category_name}</Tag>
                          </div>
                          <div style={{ marginTop: "8px" }}>
                            <Text type="secondary">
                              <strong>Duration:</strong> {item.duration}{" "}
                              {item.rental_type}(s)
                            </Text>
                          </div>
                          <div>
                            <Text type="secondary">
                              <strong>Dates:</strong> {item.start_date} to{" "}
                              {item.end_date}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                        }}
                      >
                        <Text strong style={{ fontSize: "16px" }}>
                          {parseFloat(item.total_price).toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "SAR",
                            }
                          )}
                        </Text>
                        <div style={{ marginTop: "8px" }}>
                          <Text type="secondary">
                            <strong>Qty:</strong> {item.quantity}
                          </Text>
                        </div>
                        <div>
                          <Text type="secondary">
                            <strong>Unit Price:</strong>{" "}
                            {parseFloat(item.unit_price).toLocaleString(
                              "en-US",
                              {
                                style: "currency",
                                currency: "SAR",
                              }
                            )}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Card>

          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "#fff1f0",
              borderRadius: "4px",
              border: "1px dashed #ffccc7",
            }}
          >
            <Text type="danger" style={{ fontSize: "16px" }}>
              <ExclamationCircleOutlined style={{ marginRight: "8px" }} />
              No order details found
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RenterOrderManagement;